import { getApp } from "firebase/app";
import { collection, getDocs, getFirestore, query, where, type QueryDocumentSnapshot } from "firebase/firestore";
import { getMessaging, onMessage } from "firebase/messaging";

import type { PushNotificationRule, PushNotificationTemplate, PushTriggerType } from "./types";
import { pushLogger } from "./pushLogger";

/**
 * CMS Notification Service
 *
 * Čte konfiguraci push notifikací přímo z Firestore (přímé spojení s DB)
 * bez nutnosti GraphQL endpointu. Toto je klíčová část Fáze 2.
 *
 * Firestore kolekce:
 *   pushNotificationTemplates/{id}  – texty spravované v CMS
 *   pushNotificationRules/{id}       – triggerovací pravidla spravovaná v CMS
 *
 * Číst může každý autentizovaný uživatel.
 * Psát může pouze CMS admin (server-side nebo admin SDK).
 */

// ─── Template cache ──────────────────────────────────────────────────────────

// Promise-based cache zabraňuje race condition (require-atomic-updates)
let templateCachePromise: Promise<PushNotificationTemplate[]> | null = null;

export const fetchPushTemplates = async (): Promise<PushNotificationTemplate[]> => {
  if (templateCachePromise) return await templateCachePromise;

  const db = getFirestore(getApp());
  templateCachePromise = getDocs(collection(db, "pushNotificationTemplates")).then(snap =>
    snap.docs.map((doc: QueryDocumentSnapshot) => ({
      id: doc.id,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Firestore DocumentData is untyped by design
      ...(doc.data() as unknown as Omit<PushNotificationTemplate, "id">),
    })),
  );
  return await templateCachePromise;
};

/** Vyčistí cache – vhodné při odhlášení uživatele */
export const clearTemplateCache = () => {
  templateCachePromise = null;
};

// ─── Foreground message handler ──────────────────────────────────────────────

// Singleton – zabraňuje vícenásobné registraci listeneru
let foregroundUnsubscribe: (() => void) | null = null;

/**
 * Spustí FCM foreground listener (pokud ještě neběží).
 * Bezpečné zavolat vícekrát – druhé volání je no-op.
 */
export const startForegroundMessaging = (): void => {
  pushLogger.log("startForegroundMessaging: called");
  if (foregroundUnsubscribe) {
    pushLogger.log("startForegroundMessaging: already running, skipping");
    return;
  }
  pushLogger.log("startForegroundMessaging: starting fresh");
  foregroundUnsubscribe = initForegroundMessaging();
};

/**
 * Zastaví FCM foreground listener a uvolní ho.
 */
export const stopForegroundMessaging = (): void => {
  pushLogger.log("stopForegroundMessaging: called");
  foregroundUnsubscribe?.();
  foregroundUnsubscribe = null;
};

/**
 * Poslouchá FCM zprávy, když je aplikace otevřená (foreground).
 *
 * Prohlížeč automaticky zobrazuje notifikace pouze na pozadí (SW).
 * Pro foreground musíme zobrazit notification manuálně nebo pomocí toast UI.
 *
 * Vrací unsubscribe funkci.
 */
export const initForegroundMessaging = (
  onNotification?: (title: string, body: string, data: Record<string, string | undefined>) => void,
): (() => void) => {
  pushLogger.log("initForegroundMessaging: started");
  const messaging = getMessaging(getApp());

  return onMessage(messaging, payload => {
    // AIDEV-NOTE: Titulek a text jsou v `data`, ne v `payload.notification` —
    // zprávy jsou data-only, aby SDK na pozadí nekreslilo druhou notifikaci
    // vedle naší. Viz AIDEV-NOTE v public/firebase-messaging-sw.js. [TD-306]
    const data = (payload.data ?? {}) as Record<string, string | undefined>;
    const title = data.title ?? "Todas";
    const body = data.body ?? "";
    pushLogger.log(`foreground message received — title: "${title}", tag: "${data.tag ?? "(none)"}"`);

    // Pokud je předán callback (např. napojení na toast systém), použij ho
    if (onNotification) {
      onNotification(title, body, data);
      return;
    }

    // Fallback: zobrazení přes SW (vyhýbá se no-new a funguje i offline)
    if (Notification.permission === "granted") {
      void navigator.serviceWorker.ready.then(async registration => {
        await registration.showNotification(title, {
          body,
          icon: "/android-chrome-192x192.png",
          badge: "/favicon-32x32.png",
          tag: data.tag ?? "todas-push",
        });
      });
    }
  });
};

// ─── Rule evaluation (pro Cloud Functions – pouze reference pro FE dev) ──────

/**
 * Načte aktivní pravidla z Firestore pro daný trigger typ.
 *
 * V produkci toto VOLAJÍ Cloud Functions (server-side).
 * Z FE je to k dispozici pro debugging a vývojové účely.
 */
export const fetchActiveRules = async (trigger: PushTriggerType): Promise<PushNotificationRule[]> => {
  const db = getFirestore(getApp());
  const q = query(
    collection(db, "pushNotificationRules"),
    where("enabled", "==", true),
    where("trigger", "==", trigger),
  );
  const snap = await getDocs(q);
  return snap.docs.map((doc: QueryDocumentSnapshot) => ({
    id: doc.id,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Firestore DocumentData is untyped by design
    ...(doc.data() as unknown as Omit<PushNotificationRule, "id">),
  }));
};
